// src/pages/Content.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Form, Image } from "react-bootstrap";
import { Card, CardBody } from "./../../components/card/card.jsx";
import Select from "react-select";
import { dateOptions } from "./../../components/selectvalues/selectvalue.jsx";
import glassshimmer from "../../assets/images/glassshimmer.png";
import DatePicker from "react-datepicker";
import Pagination from "./../../components/pagination/Pagination.jsx";

const Content = () => {
  const themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  // Core page state
  const [currentTab, setCurrentTab] = useState("viewed"); // viewed | clicked | shared
  const [isLoading, setIsLoading] = useState(true);

  // Server data per tab
  const [ContentViewTile, setContentViewTile] = useState([]);
  const [ContentClickedTile, setContentClickedTile] = useState([]);
  const [ContentSharedTile, setContentSharedTile] = useState([]);

  // Pagination (client)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // ── Local (editable) filter state ─────────────────────────────────────────
  const [localDateOption, setLocalDateOption] = useState(dateOptions[1]); // default: weekly (your index 1)
  const [localIsCustom, setLocalIsCustom]     = useState(false);
  const [localStartDate, setLocalStartDate]   = useState(new Date());
  const [localEndDate,   setLocalEndDate]     = useState(new Date());

  const [localTable,     setLocalTable]       = useState(0);  // 0 Blogs/News | 1 Mixlist
  const [localSearch,    setLocalSearch]      = useState("");

  // ── Applied state (what the list actually uses) ───────────────────────────
  const [appliedSearch, setAppliedSearch]     = useState(""); // search is client-side only
  const [selectedTable, setSelectedTable]     = useState(0);  // applied table (for API)

  // last applied spec: used to skip identical queries
  const lastAppliedRef = useRef(null);
  const pendingApplyRef = useRef(null);
  const [isApplying, setIsApplying] = useState(false);

  // Tabs meta
  const producttabs = [
    { id: "viewed",  label: "Viewed",  active: currentTab === "viewed"  },
    { id: "clicked", label: "Clicked", active: currentTab === "clicked" },
    { id: "shared",  label: "Shared",  active: currentTab === "shared"  },
  ];

  // Helpers
  const getFilterValue = (tab) => (tab === "clicked" ? 1 : tab === "shared" ? 2 : 0);
  const dateToTs = (d) => Math.floor(d.getTime() / 1000);

  const computeRangeFromOption = (opt, now = new Date()) => {
    const end = Math.floor(now.getTime() / 1000);
    switch (opt.value) {
      case 5:  return { start: end - 24 * 60 * 60, end }; // Today (last 24h)
      case 2:  return { start: end - 7  * 24 * 60 * 60, end };
      case 1: {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: Math.floor(d.getTime()/1000), end };
      }
      case 0: {
        const d = new Date(now.getFullYear(), 0, 1);
        return { start: Math.floor(d.getTime()/1000), end };
      }
      case 3:  // custom handled outside
      default: return { start: end - 7 * 24 * 60 * 60, end };
    }
  };

  const sameSpec = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  // ── API ───────────────────────────────────────────────────────────────────
  const ContentTileInfo = async (filterValue, startTimeSec, endTimeSec, table = 0) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.barsys.ai/barsysanalytics/public/api/contentpageinfo?startTime=${startTimeSec}&endTime=${endTimeSec}&filter=${filterValue}&table=${table}`
      );
      const result = await response.json();
      switch (filterValue) {
        case 0: setContentViewTile(result); break;
        case 1: setContentClickedTile(result); break;
        case 2: setContentSharedTile(result); break;
        default: break;
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Initial load (defaults) ───────────────────────────────────────────────
  useEffect(() => {
    // default weekly
    const { start, end } = computeRangeFromOption(dateOptions[1]);
    setLocalDateOption(dateOptions[1]);
    setLocalIsCustom(false);
    setLocalStartDate(new Date(start * 1000));
    setLocalEndDate(new Date(end * 1000));

    lastAppliedRef.current = {
      tab: currentTab,
      table: 0,
      period: dateOptions[1].value,
      start,
      end
    };
    setSelectedTable(0);
    ContentTileInfo(getFilterValue(currentTab), start, end, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Tab change: fetch immediately with last applied range & table ─────────
  useEffect(() => {
    if (!lastAppliedRef.current) return;
    const { start, end, table } = lastAppliedRef.current;
    ContentTileInfo(getFilterValue(currentTab), start, end, table);
    setCurrentPage(1);
  }, [currentTab]);

  // ── Filter controls (local only; no fetch) ────────────────────────────────
  const handleDateOptionChange = (opt) => {
    setLocalDateOption(opt);
    const isCustom = opt.value === 3;
    setLocalIsCustom(isCustom);

    if (!isCustom) {
      const { start, end } = computeRangeFromOption(opt);
      setLocalStartDate(new Date(start * 1000));
      setLocalEndDate(new Date(end * 1000));
    }
  };

  const handleLocalStart = (d) => setLocalStartDate(d);
  const handleLocalEnd   = (d) => setLocalEndDate(d);

  const handleTableChangeLocal = (e) => setLocalTable(parseInt(e.target.value, 10));
  const handleSearchChange     = (e) => setLocalSearch(e.target.value);

  // ── Apply filters button ──────────────────────────────────────────────────
  const handleApply = () => {
    // Build the next "applied" spec
    const next = (() => {
      if (localIsCustom) {
        return {
          tab: currentTab,
          table: localTable,
          period: 3,
          start: dateToTs(localStartDate),
          end:   dateToTs(localEndDate),
        };
      }
      const { start, end } = computeRangeFromOption(localDateOption);
      return {
        tab: currentTab,
        table: localTable,
        period: localDateOption.value,
        start, end
      };
    })();

    const prev = lastAppliedRef.current;
    const changedServerFilters = !prev || !sameSpec(
      { tab: next.tab, table: next.table, period: next.period, start: next.start, end: next.end },
      { tab: prev.tab, table: prev.table, period: prev.period, start: prev.start, end: prev.end }
    );
    const searchChanged = localSearch !== appliedSearch;

    // If nothing changed at all, bail
    if (!changedServerFilters && !searchChanged) return;

    // Commit client-only search immediately (no spinner if only search changed)
    if (!changedServerFilters && searchChanged) {
      setAppliedSearch(localSearch);
      setCurrentPage(1);
      return;
    }

    // Otherwise, we will fetch: show spinner first, then dispatch
    pendingApplyRef.current = next;
    setIsApplying(true);
  };

  // Post-paint: perform fetch, then clear applying when real load finishes
  useEffect(() => {
    if (!isApplying || !pendingApplyRef.current) return;

    const n = pendingApplyRef.current;
    // Commit "applied" UI state so everything stays in sync
    setSelectedTable(n.table);
    setAppliedSearch(localSearch);
    lastAppliedRef.current = n;

    // Trigger the fetch for the *current* tab
    ContentTileInfo(getFilterValue(n.tab), n.start, n.end, n.table);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApplying]);

  const prevLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      setIsApplying(false);
      pendingApplyRef.current = null;
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  // ── Derived view data (uses appliedSearch only) ───────────────────────────
  const filterBySearch = (list) =>
    list.filter((it) => (it.name || "").toLowerCase().includes(appliedSearch.toLowerCase()));

  const sourceList = currentTab === "clicked"
    ? ContentClickedTile
    : currentTab === "shared"
      ? ContentSharedTile
      : ContentViewTile;

  const filtered = useMemo(() => filterBySearch(sourceList), [sourceList, appliedSearch]);

  const indexOfLastItem  = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const pageItems        = filtered.slice(indexOfFirstItem, indexOfLastItem);

  const totalItems = filtered.length;
  const rangeStart = totalItems ? indexOfFirstItem + 1 : 0;
  const rangeEnd   = Math.min(indexOfLastItem, totalItems);

  const paginate = (p) => setCurrentPage(p);

  // Hide list while applying/loading (keeps DOM mounted to avoid wiggle)
  const hideList = isApplying || isLoading;

  return (
    <>
      <div>
        <h6>
          <span style={{ color: themeColor }}>PAGES</span> / CONTENT
        </h6>
        <div className="row">
          <div className="col-xl-8">
            <h1>CONTENT</h1>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="row gy-3 align-items-lg-end mb-3">
        {/* Date */}
        <div className="col-12 col-lg-3">
          <label className="form-label">Date</label>
          <Select
            options={dateOptions}
            value={localDateOption}
            onChange={handleDateOptionChange}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 11100 }),
              menu:       (base) => ({ ...base, zIndex: 11100 }),
            }}
          />

          {/* Custom range (stacks naturally on mobile) */}
          {localIsCustom && (
            <div className="row g-2 mt-2">
              <div className="col-6" style={{ position: 'relative', zIndex: 11000 }}>
                <label className="form-label">Date From</label>
                <DatePicker selected={localStartDate} onChange={handleLocalStart} className="form-control" />
              </div>
              <div className="col-6" style={{ position: 'relative', zIndex: 11000 }}>
                <label className="form-label">Date To</label>
                <DatePicker selected={localEndDate} onChange={handleLocalEnd} className="form-control" />
              </div>
            </div>
          )}
        </div>

        {/* Search + Button (button attached) */}
        <div className="col-12 col-lg-6">
          <label className="form-label d-lg-none">&nbsp;</label>
          <div className="input-group">
            <Form.Control
              type="text"
              value={localSearch}
              onChange={handleSearchChange}
              placeholder="Search"
            />
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => {
                setCurrentPage(1);
                handleApply();
              }}
              disabled={isApplying}
              title="Apply filters"
            >
              <i className="fas fa-search" />
            </button>
          </div>
        </div>

        {/* Radios (nice gap from the button on lg+, stack on mobile) */}
        <div className="col-12 col-lg-3">
          <div className="filter-radios d-flex align-items-center flex-wrap gap-3 ms-lg-2">
            <div className="form-check form-check-inline mb-0">
              <input
                className="form-check-input"
                type="radio"
                name="table"
                id="table-0"
                value={0}
                checked={localTable === 0}
                onChange={handleTableChangeLocal}
              />
              <label className="form-check-label" htmlFor="table-0">Blogs/News</label>
            </div>
            <div className="form-check form-check-inline mb-0">
              <input
                className="form-check-input"
                type="radio"
                name="table"
                id="table-1"
                value={1}
                checked={localTable === 1}
                onChange={handleTableChangeLocal}
              />
              <label className="form-check-label" htmlFor="table-1">Mixlist</label>
            </div>
          </div>
        </div>
      </div>



      {/* Tabs + range summary */}
      <div className="row mb-3">
        <div className="col-md-6">
          <ul className="nav nav-underline" id="myTab" role="tablist">
            {producttabs.map((tab, i) => (
              <li className="nav-item" role="presentation" key={i}>
                <button
                  className={`nav-link ${tab.active ? "active" : ""}`}
                  id={`${tab.id}-tab`}
                  data-bs-toggle="tab"
                  data-bs-target={`#${tab.id}`}
                  type="button"
                  role="tab"
                  aria-controls={tab.id}
                  aria-selected={tab.active ? "true" : "false"}
                  onClick={() => {
                    setCurrentTab(tab.id);
                    // keep local controls as-is; we fetch using last applied spec
                  }}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="col-md-6 text-end align-self-center">
          <p className="mb-0">
            {rangeStart}-{rangeEnd} of {totalItems} Records
          </p>
        </div>
      </div>

      {/* List Content */}
      <div className="col-xl-12">
        <Card className="mt-3">
          <CardBody>
            <div className="position-relative" style={{ minHeight: 240 }}>
              <div style={{ visibility: hideList ? "hidden" : "visible" }}>
                {/* Current tab panel */}
                {pageItems.length === 0 ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                    No Records Found
                  </div>
                ) : (
                  <div className="table-responsive">
                    {pageItems.map((product, index) => (
                      <div key={`${product.name}-${index}`} className="mb-3 p-3 product_border">
                        <div className="row">
                          <div className="col-xl-8 col-12 col-md-6 col">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="rounded-2 d-flex align-items-center justify-content-center me-4 w-30px h-30px text-theme-">
                                { (currentPage - 1) * itemsPerPage + index + 1 }
                              </div>
                              <div className="position-relative">
                                <Image
                                  src={product.image ? product.image : glassshimmer}
                                  className="object-fit-cover w-80px h-80px"
                                />
                              </div>
                              <div className="flex-1 ps-3">
                                <div className="fw-500 text-inverse">{product.name}</div>
                              </div>
                            </div>
                          </div>

                          <div className="col-xl-4 col-12">
                            <div className="mt-3">
                              <label>
                                {currentTab === "clicked" ? "Clicks" :
                                 currentTab === "shared"  ? "Shares" : "Views"}:
                              </label>
                              <span className="ms-3" style={{ color: themeColor }}>
                                {product.count}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {(isApplying || isLoading) && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
                  <div className="spinner-border" style={{ color: themeColor }} />
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        <div className="row mt-3">
          <div className="col-md-12 text-end">
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              paginate={paginate}
            />
          </div>
        </div>
      </div>
      <style>{`
        /* Radios sit nicely next to the search button on lg+, and stack well on mobile */
        .filter-radios .form-check { margin-bottom: .25rem; }
        .filter-radios .form-check-input { transform: translateY(1px); } /* optical alignment */

        @media (min-width: 992px) { /* lg+ */
          .filter-radios { margin-left: .25rem; } /* small, consistent gap from the search button */
        }
        @media (max-width: 575.98px) {
          .filter-radios { margin-top: .25rem; } /* breathing room under the button on small phones */
        }
      `}</style>
    </>
  );
};



export default Content;
